import { observer, useObservable } from '@legendapp/state/react'
import { MgButton, MgPopover, Paragraph, Popover, SizableText, XStack, YStack } from '@my/ui'
import { IconProps } from '@tamagui/helpers-icon'
import { Ticket } from '@tamagui/lucide-icons'
import { UserTierText } from 'app/features/ecommerce/UserTierText/UserTierText'
import { UserAvatar } from 'app/features/users/UserAvatar/UserAvatar'
import { useUserCredits } from 'app/features/users/hooks/useUserCredits'
import { useUserName } from 'app/features/users/hooks/useUserName'
import { useSupabase } from 'app/utils/supabase/useSupabase'
import { useUser } from 'app/utils/useUser'
import { useRouter as useNextRouter } from 'next/router'
import React, { useEffect } from 'react'

export const UserMenu = observer(() => {
  const open$ = useObservable(false)
  const { profile$ } = useUser()
  const router = useNextRouter()
  const userName = useUserName()
  const { creditsLeft$ } = useUserCredits()
  const supabase = useSupabase()

  useEffect(() => {
    const handleRouteChange = () => {
      open$.set(false)
    }
    router.events.on('routeChangeStart', handleRouteChange)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [open$, router.events])

  // 等待stripe回调时不展示，等待更新订阅级别
  if (!profile$.get() || router.pathname === '/payment-finished') return null

  return (
    <MgPopover open={open$.get()} onOpenChange={(v) => open$.set(v)} size="$5">
      <MgPopover.Trigger>
        <UserAvatar size="$4" $sm={{ size: '$3' }} cur="pointer" useSelfInfo />
      </MgPopover.Trigger>
      <MgPopover.Content theme="light">
        <Popover.Arrow borderWidth={1} boc="$borderColor" />
        <YStack jc="center" ai="stretch" w="100%" minWidth={240}>
          <YStack ai="center" mb="$3" gap="$1.5">
            <Paragraph size="$5" fow="700">
              @ {userName}
            </Paragraph>
            <SizableText
              backgroundColor="$blue9"
              size="$1"
              px="$2"
              br="$5"
              color="$blue1"
              fow="700"
            >
              <UserTierText />
            </SizableText>
            <Paragraph fow="700" my="$2">
              {creditsLeft$.get()} cedits left
            </Paragraph>
          </YStack>
          <MenuItem icon={Ticket} text="Get Subscription(coming soon)" onPress={() => {}} />
          <XStack theme="dark">
            <MgButton.Default w="100%" size="$3" mt="$5" onPress={() => supabase.auth.signOut()}>
              <MgButton.Text>Log Out</MgButton.Text>
            </MgButton.Default>
          </XStack>
        </YStack>
      </MgPopover.Content>
    </MgPopover>
  )
})

const MenuItem = ({
  icon: Icon,
  text,
  onPress,
  children,
}: {
  icon: React.FC<IconProps>
  text: string
  onPress: () => void
  children?: React.ReactNode
}) => {
  return (
    <XStack
      ai="center"
      jc="center"
      gap="$2"
      w="100%"
      px="$3"
      py="$2.5"
      bg="$gray5"
      my="$1.5"
      br="$3"
      cur="pointer"
      hoverStyle={{ bg: '$gray7' }}
      onPress={onPress}
    >
      <Icon size="$1" />
      <Paragraph fow="700">{text}</Paragraph>
      {children}
    </XStack>
  )
}
